// External Imports
import { useState, useEffect, Fragment, useContext } from "react";

// Internal Imports
import { requestAdminAPI } from "../../api/posh-api";
import { getISODate, isoDateToString } from "../../util/format-date";
import DetailPair from "../UI/DetailPair";
import LinkPair from "../UI/LinkPair";
import Heading from "../UI/Heading";
import { MdCheck, MdClose, MdEdit, MdRefresh } from "react-icons/md";
import uploadFile from "../../util/upload-file";
import { useSearchParams } from "react-router-dom";
import PersonCard from "../UI/PersonCard";
import ImageSelector from "../UI/ImageSelector";
import { UtilityContext } from "../../context/util-context";
import Notes from "../UI/Notes";
import AddExpense from "../expenses-page/AddExpense";
import { formatCurrency, toTitleCase } from "../../util/format-text";
import SelectPair from "../UI/SelectPair";
import BankAccountSelector from "./BankAccountSelector";
import AddBankAccount from "../partner-page/AddBankAccountModal";
import AddLenderModal from "./AddLenderModal";
import AuditLogs from "../UI/AuditLogs";
import MarketValue from "./MarketValue";

/** ========================================================================
 * * Application Info
 * Renders a component, which allows the user to view or edit a partner's application.
 * ========================================================================== */

export default function ApplicationInfo() {
  // Load Context
  const { navigate, setConfirm } = useContext(UtilityContext);

  // Get Vehicle ID from URL
  const [searchParms] = useSearchParams();
  const id = searchParms.get("id");

  // Initialize States
  const [loading, setLoading] = useState(false);
  const [edit, setEdit] = useState(false);
  const [application, setApplication] = useState({});
  const [lenders, setLenders] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [item, setItem] = useState();
  const [payment, setPayment] = useState(false);
  const [invoice, setInvoice] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showLenderModal, setShowLenderModal] = useState(false);

  /* ======================= FETCH LENDERS ======================= */
  const fetchLenders = async () => {
    try {
      setLoading(true);
      const lenders = await requestAdminAPI("fetch-all", {
        table: "lender",
      });
      setLenders(lenders);
      setEdit(true);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ======================= FETCH APPLICATION ======================= */
  const fetchApplication = async () => {
    try {
      setLoading(true);
      const res = await requestAdminAPI("fetch", {
        table: "application",
        id: id,
        columns: [
          "vehicle { id cornerImage }",
          "partner { id name type photo }",
          "account { bank account routing type }",
          "lender { id name }",
          "createdBy { id name title photo }",
          "activatedBy { id name title photo }",
          "deactivatedBy { id name title photo }",
        ],
      });
      // If application not found, redirect to applications list
      if (!res || !res.id) {
        navigate("/applications");
        return;
      }
      setApplication(res);
      setLoading(false);
    } catch (err) {
      console.error(err);
      // If application not found or any error, redirect to applications list
      // This handles cases where user navigates to a deleted application
      if (err.message?.includes("not found") || err.message?.includes("No Record")) {
        navigate("/applications");
      }
      setLoading(false);
    }
  };

  /* ======================= UPDATE VEHICLE APPLICATION ======================= */
  const updateApplication = async () => {
    try {
      setLoading(true);
      const payload = {
        applicationId: id,
        partnerId: application.partnerId,
        license: document.getElementById("license").value,
        licenseState: document.getElementById("licenseState").value,
        lenderId: document.getElementById("lender").value,
        financeAmount: document.getElementById("financeAmount").value,
        loanTerm: document.getElementById("loanTerm").value,
        monthlyPayment: document.getElementById("monthlyPayment").value,
        dueDate:
          document.getElementById("dueDate").value &&
          getISODate(document.getElementById("dueDate").value),
        registerationExp:
          document.getElementById("registerationExp").value &&
          getISODate(document.getElementById("registerationExp").value),
        insuranceExp:
          document.getElementById("insuranceExp").value &&
          getISODate(document.getElementById("insuranceExp").value),
        type: document.getElementById("type").value,
        loanAccount: document.getElementById("loanAccount").value,
      };

      // Get Files
      const files = {
        title: document.getElementById("title").files[0],
        registeration: document.getElementById("registeration").files[0],
        insurance: document.getElementById("insurance").files[0],
        billOfSale: document.getElementById("billOfSale").files[0],
        vehicleInspectionReport: document.getElementById(
          "vehicleInspectionReport",
        ).files[0],
        loanDocument: document.getElementById("loanDocument").files[0],
        powerOfAttorney: document.getElementById("powerOfAttorney").files[0],
        agreement: document.getElementById("agreement").files[0],
      };
      // Upload Files
      for (const key in files) {
        if (files[key]) {
          const url = await uploadFile(
            `partners/${id}/${key}`,
            files[key],
            setUploading,
          );
          setItem(key);
          payload[key] = url;
        }
      }

      // Send Request
      await requestAdminAPI("manage-application", payload);
      fetchApplication();
      setEdit(false);
      setUploading();
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ======================= REASSIGN DOCUMENT HANDLER =======================
  Allows admin to reassign a power of attorney or vehicle agreement after 
  changes are made by deleting the existing document.
   */
  const reassignDocumentHandler = async (key) => {
    try {
      setLoading(true);
      // Send Request
      await requestAdminAPI("manage-application", {
        applicationId: id,
        partnerId: application.partnerId,
        deleteItem: key,
      });
      fetchApplication();
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ======================= ACTIVATE/DEACTIVATE VEHICLE ======================= */
  const activateDeactivateHandler = async () => {
    try {
      setLoading(true);
      // Send Request
      await requestAdminAPI("approve-application", {
        id: id,
      });
      fetchApplication();
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ======================= DELETE APPLICATION ======================= */
  const deleteApplication = async () => {
    try {
      setLoading(true);
      await requestAdminAPI("manage-application", {
        applicationId: id,
        partnerId: application.partnerId,
        toDelete: true,
      });
      navigate("/applications");
      setLoading(false);
    } catch (err) {
      console.error(err);
      // Navigate away even on error since deletion might have succeeded
      // This prevents user from staying on a deleted application page
      navigate("/applications");
      setLoading(false);
    }
  };

  // Fetch Partner on Load
  useEffect(() => {
    if (id) fetchApplication();
  }, [id]);

  return (
    <div className="divider">
      {(payment || invoice) && (
        <AddExpense
          open={payment || invoice}
          setOpen={payment ? setPayment : setInvoice}
          defaultCategory={
            invoice
              ? "application-reimbursement-64200"
              : "application-installment-64200"
          }
          defaultSelected={application.id}
          defaultDescription={
            payment
              ? `Installment payment for ${application?.year} ${
                  application?.make
                } ${application?.model} - ${application?.vin?.substring(
                  13,
                  17,
                )}`
              : ""
          }
          defaultAmount={payment ? application?.monthlyPayment : ""}
          onComplete={fetchApplication}
          defaultPaidVia="_zelle"
          pending={payment}
        />
      )}
      <AddBankAccount
        open={showAddModal}
        partnerId={application?.partnerId}
        setOpen={setShowAddModal}
        refresh={() => {}}
      />
      <AddLenderModal
        open={showLenderModal}
        setOpen={setShowLenderModal}
        refresh={fetchLenders}
      />
      <div className="container gap-6 lg:col-span-3">
        <Heading
          title="Application"
          text="Activating application will replace the vehicle's License Plate
              information with the information below. Making changes to an onboarded
              application directly affects the onboarded vehicle."
          id={id}
        >
          {edit ? (
            <Fragment>
              <MdCheck className="icon" onClick={updateApplication} />
              <MdClose className="icon" onClick={() => setEdit(false)} />
            </Fragment>
          ) : (
            <MdEdit className="icon" onClick={fetchLenders} />
          )}
          <AuditLogs table="application" id={id} />
          <MdRefresh className="icon" onClick={fetchApplication} />
        </Heading>
        <ApplicationDetails
          application={application}
          loading={loading}
          edit={edit}
          lenders={lenders}
          setShowAddModal={setShowAddModal}
        />
        <MarketValue application={application} refresh={fetchApplication} />
        <Notes table="application" id={id} />
      </div>

      <div className="sidebar">
        <div className="grid gap-6 overflow-scroll no-scrollbar">
          <PersonCard
            label="Partner:"
            name={application.partner?.name}
            secondary={application.partner?.type}
            image={application.partner?.photo}
            link={`/partners/info?id=${application.partner?.id}`}
            loading={loading}
          />
          {application.vehicle && (
            <div
              className="clickable"
              onClick={() =>
                navigate(`/vehicles/info?id=${application.vehicle?.id}`)
              }
            >
              <ImageSelector
                image={application.vehicle?.cornerImage}
                loading={loading}
                classes="w-full min-h-[150px]"
              />
            </div>
          )}

          <PersonCard
            label="Created by:"
            name={
              application.createdBy
                ? application.createdBy?.name
                : application.partner?.name
            }
            secondary={
              application.createdBy ? application.createdBy?.title : "FAP"
            }
            image={
              application.createdBy
                ? application.createdBy?.photo
                : application.partner?.photo
            }
            link={
              application.createdBy
                ? `/employees/info?id=${application.createdBy?.id}`
                : `/partners/info?id=${application.partner?.id}`
            }
            loading={loading}
          />
          {application.onboarded ? (
            <PersonCard
              label="Activated By:"
              name={application.activatedBy?.name}
              secondary={isoDateToString(
                application.activatedOn,
                "MM/dd/yyyy hh:mm a",
              )}
              image={application.activatedBy?.photo}
              link={`/employees/info?id=${application.activatedBy?.id}`}
              loading={loading}
            />
          ) : (
            <PersonCard
              label="Deactivated By:"
              name={application.deactivatedBy?.name}
              secondary={isoDateToString(
                application.deactivatedOn,
                "MM/dd/yyyy hh:mm a",
              )}
              image={application.deactivatedBy?.photo}
              link={`/employees/info?id=${application.deactivatedBy?.id}`}
              loading={loading}
            />
          )}
        </div>

        {loading ? (
          <div className="grid gap-2">
            {Array.from({ length: 3 }, (_, i) => (
              <div key={i} className="h-9 btn-secondary animate-pulse"></div>
            ))}
          </div>
        ) : (
          <div className="grid gap-2">
            {uploading && (
              <p className="mb-2 text-stone-400">
                Uploading {item}... {uploading}%
              </p>
            )}
            {application?.onboarded && (
              <div onClick={() => setPayment(true)} className="btn-primary">
                Log Payment
              </div>
            )}
            {application?.onboarded && (
              <div onClick={() => setInvoice(true)} className="btn-secondary">
                Log Reimbursement
              </div>
            )}
            <div
              onClick={() => setShowLenderModal(true)}
              className="btn-secondary"
            >
              Add Lender
            </div>
            {application?.powerOfAttorney && (
              <div
                onClick={() => reassignDocumentHandler("powerOfAttorney")}
                className="btn-secondary"
              >
                Reassign POA
              </div>
            )}
            {application?.agreement && (
              <div
                onClick={() => reassignDocumentHandler("agreement")}
                className="btn-secondary"
              >
                Reassign Agreement
              </div>
            )}
            {application?.onboarded ? (
              <div
                onClick={() => activateDeactivateHandler("inactive")}
                className="btn-warning"
              >
                Deactivate Vehicle
              </div>
            ) : (
              <Fragment>
                <div
                  onClick={() => activateDeactivateHandler("active")}
                  className="btn-primary"
                >
                  Activate Vehicle
                </div>
                <div
                  onClick={() => deleteApplication()}
                  className="btn-warning"
                >
                  Delete Application
                </div>
              </Fragment>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** ========================================================================
 * * Application Details
 * ========================================================================== */
const ApplicationDetails = ({
  application,
  loading,
  edit,
  lenders,
  setShowAddModal,
}) => {
  return (
    <div className="info">
      <DetailPair
        loading={loading}
        label="Date Created"
        edit={true}
        inputOptions={{
          disabled: true,
          value: isoDateToString(application.dateCreated),
        }}
      />
      <DetailPair
        loading={loading}
        label="VIN"
        edit={true}
        inputOptions={{
          disabled: true,
          value: application.vin,
        }}
      />
      <DetailPair
        loading={loading}
        label="Vehicle"
        edit={true}
        value={`${application.year} ${application.make} ${application.model}`}
        inputOptions={{
          disabled: true,
          value: `${application.year} ${application.make} ${application.model}`,
        }}
      />
      <DetailPair
        loading={loading}
        label="Status"
        value={application?.onboarded ? "ONBOARDED" : "Not Onboarded"}
        classes={
          application?.onboarded
            ? "font-semibold text-green-600"
            : "text-stone-200"
        }
      />
      <DetailPair
        loading={loading}
        label="License"
        value={application?.license}
        edit={edit}
        inputOptions={{
          id: "license",
          defaultValue: application?.license,
        }}
      />
      <DetailPair
        loading={loading}
        label="License State"
        value={application?.licenseState}
        edit={edit}
        inputOptions={{
          id: "licenseState",
          defaultValue: application?.licenseState,
        }}
      />
      <LinkPair
        loading={loading}
        label="Title"
        link={application?.title}
        edit={edit}
        inputOptions={{
          type: "file",
          id: "title",
        }}
      />
      <LinkPair
        loading={loading}
        label="Bill of Sale"
        link={application?.billOfSale}
        edit={edit}
        inputOptions={{
          type: "file",
          id: "billOfSale",
        }}
      />
      <LinkPair
        loading={loading}
        label="Registration"
        link={application?.registeration}
        edit={edit}
        inputOptions={{
          type: "file",
          id: "registeration",
        }}
      />
      <DetailPair
        loading={loading}
        label="Registration Expiration"
        classes={
          application?.registerationExp &&
          (new Date(application.registerationExp) < new Date()
            ? "animate-pulse warning"
            : "")
        }
        value={isoDateToString(application?.registerationExp)}
        edit={edit}
        inputOptions={{
          type: "date",
          id: "registerationExp",
          defaultValue: isoDateToString(
            application?.registerationExp,
            "yyyy-MM-dd",
          ),
        }}
      />
      <LinkPair
        loading={loading}
        label="Insurance"
        link={application?.insurance}
        edit={edit}
        inputOptions={{
          type: "file",
          id: "insurance",
        }}
      />
      <DetailPair
        loading={loading}
        label="Insurance Expiration"
        classes={
          application?.insuranceExp &&
          (new Date(application.insuranceExp) < new Date()
            ? "animate-pulse warning"
            : "")
        }
        value={isoDateToString(application?.insuranceExp)}
        edit={edit}
        inputOptions={{
          type: "date",
          id: "insuranceExp",
          defaultValue: isoDateToString(
            application?.insuranceExp,
            "yyyy-MM-dd",
          ),
        }}
      />
      <LinkPair
        loading={loading}
        label="Inspection Report"
        link={application?.vehicleInspectionReport}
        edit={edit}
        inputOptions={{
          type: "file",
          id: "vehicleInspectionReport",
        }}
      />
      <LinkPair
        loading={loading}
        label="Power of Attorney"
        link={application?.powerOfAttorney}
        edit={edit}
        inputOptions={{
          type: "file",
          id: "powerOfAttorney",
        }}
      />
      <LinkPair
        loading={loading}
        label="Vehicle Agreement"
        link={application?.agreement}
        edit={edit}
        inputOptions={{
          type: "file",
          id: "agreement",
        }}
      />
      <LinkPair
        loading={loading}
        label="Loan Document"
        edit={edit}
        link={application?.loanDocument}
        inputOptions={{
          type: "file",
          id: "loanDocument",
        }}
      />
      <DetailPair
        loading={loading}
        label="Finance Amount"
        value={formatCurrency(application?.financeAmount)}
        edit={edit}
        inputOptions={{
          id: "financeAmount",
          defaultValue: application?.financeAmount,
        }}
      />
      <DetailPair
        loading={loading}
        label="Term (Months)"
        value={application?.loanTerm}
        edit={edit}
        inputOptions={{
          id: "loanTerm",
          defaultValue: application?.loanTerm,
        }}
      />
      <DetailPair
        loading={loading}
        label="Monthly Payment"
        value={formatCurrency(application?.monthlyPayment)}
        edit={edit}
        inputOptions={{
          id: "monthlyPayment",
          defaultValue: application?.monthlyPayment,
        }}
      />
      <DetailPair
        loading={loading}
        label="Due Date"
        value={isoDateToString(application?.dueDate)}
        edit={edit}
        inputOptions={{
          type: "date",
          id: "dueDate",
          defaultValue: isoDateToString(application?.dueDate, "yyyy-MM-dd"),
        }}
      />
      <SelectPair
        loading={loading}
        label="Contract Type"
        options={[
          { name: "Financed", id: "financed" },
          { name: "Leased", id: "leased" },
        ]}
        value={toTitleCase(application?.type)}
        inputOptions={{
          id: "type",
          defaultValue: application?.type,
        }}
        edit={edit}
      />
      <SelectPair
        loading={loading}
        label="Lender (Financing Institution)"
        value={application?.lender?.name}
        edit={edit}
        options={lenders.map((lender) => ({
          name: lender.name,
          id: lender.id,
        }))}
        inputOptions={{
          id: "lender",
          defaultValue: application?.lender?.id,
        }}
      />
      <DetailPair
        loading={loading}
        label="Loan Account Number"
        value={application.loanAccount}
        edit={edit}
        inputOptions={{
          id: "loanAccount",
          defaultValue: application.loanAccount,
        }}
      />
      {edit ? (
        <div className="lg:col-span-2">
          <BankAccountSelector
            onSelect={() => {}}
            selected={application?.account?.id}
            addHandler={() => setShowAddModal(true)}
          />
        </div>
      ) : (
        <Fragment>
          <DetailPair
            loading={loading}
            label="Bank Name"
            value={`${application.account?.bank || ""} ${
              application.account?.type || "-"
            }`}
          />
          <DetailPair
            loading={loading}
            label="Bank Account Number"
            value={application.account?.account}
          />
          <DetailPair
            loading={loading}
            label="Routing Number"
            value={application.account?.routing}
          />
        </Fragment>
      )}
      <DetailPair
        loading={loading}
        label="Last Payment Date"
        value={isoDateToString(application?.lastPayment)}
      />
    </div>
  );
};
